import { SurfaceCard } from "@bednbody/ui/components/surface-card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@bednbody/ui/components/empty";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@bednbody/ui/components/avatar";
import { Button } from "@bednbody/ui/components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@bednbody/ui/components/dropdown-menu";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from "@bednbody/ui/components/combobox";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@bednbody/ui/components/table";
import { cn } from "@bednbody/ui/lib/utils";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CaretUpDownIcon,
  DownloadSimpleIcon,
  DotsThreeVerticalIcon,
  MagnifyingGlassIcon,
} from "@phosphor-icons/react";
import {
  Fragment,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Badge } from "./badge";

export interface AdminTableFacet<T> {
  id: string;
  label: string;
  options: readonly { value: string; label: string }[];
  getValue: (row: T) => string;
}

export interface AdminTableExportColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

export interface AdminDataTableProps<T> {
  /** contained preserves the original frame; standalone separates controls from the table surface. */
  presentation?: "contained" | "standalone" | "embedded";
  data: T[];
  columns: ColumnDef<T, unknown>[];
  getRowId: (row: T) => string;
  searchText: (row: T) => string;
  searchPlaceholder?: string;
  facets?: readonly AdminTableFacet<T>[];
  /** @deprecated Facets use searchable comboboxes; this setting is ignored. */
  facetCollapseAfter?: number;
  initialFacets?: Record<string, string>;
  exportColumns?: readonly AdminTableExportColumn<T>[];
  onExport?: (sortedMatchingRows: T[]) => void;
  exportFilename?: string;
  exportScopeLabel?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  loading?: boolean;
  initialSorting?: SortingState;
  /** A heading above the toolbar — the table's own section title. */
  title?: ReactNode;
  description?: ReactNode;
  /** Extra controls in the toolbar, after the filters (a date range, a select…). */
  header?: ReactNode;
  /**
   * Makes the whole row open something. A click that starts on a control inside
   * the row (a menu, a button, a link, an input) is left to that control, and so
   * is anything rendered in a portal from the row, such as a dialog.
   */
  onRowClick?: (row: T) => void;
  /** Accessible name for a clickable row, e.g. "Open Ariel Aguirre". */
  rowLabel?: (row: T) => string;
  renderExpandedRow?: (row: T) => ReactNode;
  isRowExpanded?: (row: T) => boolean;
  className?: string;
}

function csvCell(value: string | number | null | undefined): string {
  let text = String(value ?? "");
  // Spreadsheet programs execute cells beginning with these characters.
  if (/^[\s]*[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}

function downloadCsv<T>(rows: T[], columns: readonly AdminTableExportColumn<T>[], filename: string) {
  const csv = [
    columns.map((column) => csvCell(column.header)).join(","),
    ...rows.map((row) => columns.map((column) => csvCell(column.value(row))).join(",")),
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Identity cell shared by people, bookings, and other record listings. */
export function AdminTableIdentity({
  title,
  subtitle,
  image,
  fallback,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  image?: string | null;
  fallback: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 items-center gap-3", className)}>
      <Avatar className="bg-primary/10">
        {image ? <AvatarImage src={image} alt="" /> : null}
        <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary-dark dark:text-primary-light">
          {fallback}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="truncate font-medium text-foreground">{title}</p>
        {subtitle ? <p className="truncate text-xs text-muted-foreground">{subtitle}</p> : null}
      </div>
    </div>
  );
}

export function AdminTableRowMenu({
  label,
  actions,
}: {
  label: string;
  actions: readonly { label: string; onSelect: () => void; disabled?: boolean }[];
}) {
  return (
    <div onClick={(event) => event.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger render={<Button type="button" variant="ghost" size="icon" aria-label={`Actions for ${label}`} />}>
          <DotsThreeVerticalIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {actions.map((action) => (
            <DropdownMenuItem key={action.label} disabled={action.disabled} onClick={action.onSelect}>
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const INTERACTIVE =
  "button, a, input, select, textarea, label, [role='menuitem'], [role='checkbox'], [data-row-click-ignore]";

/** True when a click on a row should open it: not on a control, not from a portal. */
function isRowActivation(event: MouseEvent<HTMLTableRowElement>): boolean {
  const row = event.currentTarget;
  const target = event.target as HTMLElement;
  // React bubbles portal events up the COMPONENT tree, so a click inside a dialog
  // opened from this row would otherwise reach it. The DOM tree knows better.
  if (!row.contains(target)) return false;
  const control = target.closest(INTERACTIVE);
  return control === null || !row.contains(control);
}

// One shared empty list: a fresh `[]` default on every render would change the
// filtered rows' identity each time, and the table's page reset would then
// re-render forever.
const NO_FACETS: readonly never[] = [];

export function AdminDataTable<T>({
  presentation = "contained",
  data,
  columns,
  getRowId,
  searchText,
  searchPlaceholder = "Search records…",
  facets = NO_FACETS,
  initialFacets = {},
  exportColumns,
  onExport,
  exportFilename = "records",
  exportScopeLabel,
  emptyTitle = "No matching records",
  emptyDescription = "Try a different search or filter.",
  loading = false,
  initialSorting = [],
  title,
  description,
  header,
  onRowClick,
  rowLabel,
  renderExpandedRow,
  isRowExpanded,
  className,
}: AdminDataTableProps<T>) {
  const TableSurface = presentation === "standalone" ? SurfaceCard : "div";
  const pageSizeId = useId();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Record<string, string>>(initialFacets);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const searched = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return query ? data.filter((row) => searchText(row).toLocaleLowerCase().includes(query)) : data;
  }, [data, search, searchText]);
  const filtered = useMemo(
    () =>
      searched.filter((row) =>
        facets.every((facet) => !selected[facet.id] || facet.getValue(row) === selected[facet.id]),
      ),
    [facets, searched, selected],
  );
  const table = useReactTable({
    data: filtered,
    columns,
    getRowId: (row) => getRowId(row),
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  const sortedRows = table.getPrePaginationRowModel().rows;
  const pageRows = table.getRowModel().rows;
  const start = filtered.length === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const end = Math.min(filtered.length, (pagination.pageIndex + 1) * pagination.pageSize);
  const hasExport = Boolean(exportColumns?.length || onExport);

  const updateSearch = (value: string) => {
    setSearch(value);
    table.setPageIndex(0);
  };
  const updateFacet = (id: string, value: string) => {
    setSelected((current) => {
      const next = { ...current };
      if (!value) delete next[id];
      else next[id] = value;
      return next;
    });
    table.setPageIndex(0);
  };

  return (
    <section
      className={cn(
        "min-w-0 max-w-full text-card-foreground",
        presentation === "contained"
          ? "overflow-hidden rounded-xl border border-border bg-card shadow-xs"
          : "flex flex-col gap-4",
        className,
      )}
    >
      {title ? (
        <div className={cn("min-w-0", presentation === "contained" ? "px-5 pt-5" : null)}>
          <h3 className="text-base font-semibold tracking-tight text-foreground">{title}</h3>
          {description ? <p className="mt-0.5 type-body-default text-muted-foreground">{description}</p> : null}
        </div>
      ) : null}

      <div
        className={cn(
          "flex min-w-0 flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between",
          presentation === "contained" ? "p-4" : null,
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-3 lg:basis-96 lg:flex-row lg:items-center">
          <div className="relative min-w-0 lg:w-64 lg:shrink-0">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => updateSearch(event.target.value)}
              className="w-full pl-9"
            />
          </div>
          {facets.length > 0 ? (
            <div
              className="flex min-w-0 flex-wrap items-center gap-2"
              aria-label="Table filters"
            >
              {facets.map((facet) => {
                const candidates = searched.filter((row) =>
                  facets.every(
                    (other) =>
                      other.id === facet.id || !selected[other.id] || other.getValue(row) === selected[other.id],
                  ),
                );
                const current = selected[facet.id] ?? "";
                const items = [
                  { value: "", label: "All", count: candidates.length },
                  ...facet.options
                    .map((option) => ({
                      ...option,
                      count: candidates.filter((row) => facet.getValue(row) === option.value).length,
                    }))
                    .filter((option) => option.count > 0 || option.value === current),
                ];
                const active = items.find((item) => item.value === current);
                return (
                  <Combobox
                    key={facet.id}
                    items={items}
                    value={active ?? null}
                    itemToStringLabel={(item) => item.label}
                    itemToStringValue={(item) => item.value}
                    onValueChange={(item) => updateFacet(facet.id, item?.value ?? "")}
                  >
                    <ComboboxTrigger
                      render={
                        <Button
                          type="button"
                          variant="outline"
                          className="w-48 min-w-0 max-w-full justify-between font-normal"
                          aria-label={`${facet.label}: ${active?.label ?? current}`}
                        />
                      }
                    >
                      <span className="min-w-0 truncate">
                        {facet.label}: {active?.label ?? current}
                      </span>
                    </ComboboxTrigger>
                    <ComboboxContent>
                      <ComboboxInput
                        aria-label={`Search ${facet.label}`}
                        placeholder={`Search ${facet.label}...`}
                        showTrigger={false}
                      />
                      <ComboboxEmpty>No options found.</ComboboxEmpty>
                      <ComboboxList>
                        {(item) => (
                          <ComboboxItem key={item.value || "__all"} value={item}>
                            <span className="min-w-0 flex-1 break-words">{item.label}</span>
                            <Badge variant="secondary">{item.count}</Badge>
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                );
              })}
            </div>
          ) : null}
        </div>
        {header || hasExport ? (
          <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-2 lg:justify-end">
            {header ? <div className="min-w-0">{header}</div> : null}
            {hasExport ? (
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                onClick={() => {
                  const rows = sortedRows.map((row) => row.original);
                  if (onExport) onExport(rows);
                  else if (exportColumns) downloadCsv(rows, exportColumns, exportFilename);
                }}
                disabled={filtered.length === 0}
                title={exportScopeLabel ?? "Export matching loaded rows"}
              >
                <DownloadSimpleIcon /> Export
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {loading ? <span className="sr-only" role="status">Loading records...</span> : null}
      <TableSurface
        aria-busy={loading}
        data-slot="admin-table-surface"
        className={cn(
          "min-w-0 max-w-full overflow-hidden",
          presentation === "contained" ? "mx-4 rounded-lg border border-border" : null,
          presentation === "standalone" ? "p-0" : null,
        )}
      >
        <Table>
          <TableHeader className="bg-muted/50">
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id} className="hover:bg-transparent">
                {group.headers.map((head) => (
                  <TableHead
                    key={head.id}
                    className="h-10 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                    aria-sort={
                      head.column.getIsSorted() === "asc"
                        ? "ascending"
                        : head.column.getIsSorted() === "desc"
                          ? "descending"
                          : "none"
                    }
                  >
                    {head.isPlaceholder ? null : head.column.getCanSort() ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-sm uppercase hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={head.column.getToggleSortingHandler()}
                      >
                        {flexRender(head.column.columnDef.header, head.getContext())}
                        {head.column.getIsSorted() === "asc" ? (
                          <ArrowUpIcon className="size-3.5 text-foreground" />
                        ) : head.column.getIsSorted() === "desc" ? (
                          <ArrowDownIcon className="size-3.5 text-foreground" />
                        ) : (
                          <CaretUpDownIcon className="size-3.5 opacity-60" />
                        )}
                      </button>
                    ) : (
                      flexRender(head.column.columnDef.header, head.getContext())
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }, (_, index) => (
                <TableRow key={index} className="hover:bg-transparent">
                  {table.getVisibleLeafColumns().map((column) => (
                    <TableCell key={column.id} className="px-4 py-3">
                      <Skeleton aria-hidden className="h-4 w-3/4 motion-reduce:animate-none" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : pageRows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={table.getVisibleLeafColumns().length}>
                  <Empty>
                    <EmptyHeader>
                      <EmptyTitle>{emptyTitle}</EmptyTitle>
                      <EmptyDescription>{emptyDescription}</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    className={cn(
                      "transition-colors hover:bg-muted/40",
                      onRowClick
                        ? "cursor-pointer focus-visible:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                        : null,
                    )}
                    tabIndex={onRowClick ? 0 : undefined}
                    aria-label={onRowClick ? rowLabel?.(row.original) : undefined}
                    onClick={
                      onRowClick
                        ? (event: MouseEvent<HTMLTableRowElement>) => {
                            if (isRowActivation(event)) onRowClick(row.original);
                          }
                        : undefined
                    }
                    onKeyDown={
                      onRowClick
                        ? (event: KeyboardEvent<HTMLTableRowElement>) => {
                            // Only when the row itself has focus — Enter on a
                            // button inside it belongs to that button.
                            if (event.target !== event.currentTarget) return;
                            if (event.key !== "Enter" && event.key !== " ") return;
                            event.preventDefault();
                            onRowClick(row.original);
                          }
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="px-4 py-3">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderExpandedRow && isRowExpanded?.(row.original) ? (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={columns.length} className="bg-muted/20 p-0">
                        {renderExpandedRow(row.original)}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableSurface>

      <div
        className={cn(
          "flex min-w-0 flex-wrap items-center justify-between gap-3 type-body-default text-muted-foreground",
          presentation === "contained" ? "px-5 py-3" : null,
        )}
      >
        <p className="tabular-nums">
          {start}–{end} of {filtered.length}
          {exportScopeLabel ? ` · ${exportScopeLabel}` : ""}
        </p>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Label htmlFor={pageSizeId}>Rows</Label>
          <NativeSelect
            id={pageSizeId}
            size="sm"
            value={pagination.pageSize}
            onChange={(event) => table.setPageSize(Number(event.target.value))}
          >
            <NativeSelectOption value={10}>10</NativeSelectOption>
            <NativeSelectOption value={25}>25</NativeSelectOption>
            <NativeSelectOption value={50}>50</NativeSelectOption>
          </NativeSelect>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Previous page"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <CaretLeftIcon />
          </Button>
          <span className="min-w-12 text-center tabular-nums">
            {table.getPageCount() === 0 ? 0 : pagination.pageIndex + 1} / {table.getPageCount()}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Next page"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <CaretRightIcon />
          </Button>
        </div>
      </div>
    </section>
  );
}
